import { EventEmitter, inject } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { LoginService, PopUpDialogInformalService, PosDataService, SharedService } from "@dert-services";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { DropdownOption } from "src/app/components/ui-components/dropdown/dropdown.model";
import { PopUpInformalType } from "src/app/components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model";
import { DertApiService } from "src/app/services/api/dert.service";
import { SharedApiService } from "src/app/services/api/sharedApi.service";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { CreateFinancialTransactionRequest, DenominationItem, TransactionTypeCodes } from "src/app/services/model/create-financial-transaction.model";
import { FinancialDocumentConfig, FinancialDocumentTypeConfig, FinancialTransactionType, TenderMovementReasonConfig, TenderMovementReasonsConfig } from "src/app/services/model/financial-transaction-config";
import { ReportExportService } from "src/app/services/report-export.service";
import { HeaderProcessingService } from "src/app/services/header-processing.service";

export class FinancialDocumentBase {
    private dertApi = inject(DertApiService);
    protected readonly spinner = inject(NgxSpinnerService);
    protected finDocConfig: FinancialDocumentTypeConfig;
    protected finDocType: FinancialTransactionType;
    protected form: FormGroup;
    protected readonly translate = inject(TranslateService);
    protected readonly loginService = inject(LoginService);
    protected readonly sharedApiService = inject(SharedApiService);
    protected readonly _sharedService = inject(SharedService);
    protected readonly reportExportService = inject(ReportExportService);
    protected readonly headerProcessingService = inject(HeaderProcessingService);
    protected readonly popUpDialogInformalService = inject(PopUpDialogInformalService);
    protected readonly posDataService = inject(PosDataService);
    protected onClose: EventEmitter<void>;

    reasonOptions: DropdownOption[] = [];

    constructor()
    {

    }

    public async getConfig(type: FinancialTransactionType): Promise<FinancialDocumentTypeConfig>
    {
        const configs = (await this.dertApi.loadConfig('NG.Shared.Models.Config.FinancialDocumentConfig')).config as FinancialDocumentConfig;
        const res = configs.configurations.find( c => c.type == type);
        return res;
    }

    public async getReasonsConfig(type: FinancialTransactionType): Promise<TenderMovementReasonConfig[]>
    {
        const configs = (await this.dertApi.loadConfig('NG.Shared.Models.Config.TenderMovementReasonsConfig')).config as TenderMovementReasonsConfig;
        const reasons = configs.movementReasons.filter( t => !!t.enabledForTypes.find( e => e == type ));
        return reasons;
    }


    protected async loadData() {
        this.spinner.show();

        this.finDocConfig = await this.getConfig(this.finDocType);
        const reasons = await this.getReasonsConfig(this.finDocType);
        this.reasonOptions = [];
        reasons.forEach( r => {
            this.reasonOptions.push(<DropdownOption> {
                    value: r.code,
                    label: r.description
            })
        });
        this.spinner.hide();
    }    

    async createFinancialTransaction(transactionTypeCode: TransactionTypeCodes,
                                     tenderType: TenderType,
                                     tenderSubType: string,
                                     totalPrice: number = null,
                                     fixedReason: string = null,
                                     attributes: { [key: string]: string } = null,
                                     currencyCode: string = null,
                                     additionalJsonData: string = '',
                                     denominationItems: DenominationItem[] = []) {
        this.spinner.show();

        const request = <CreateFinancialTransactionRequest> {
            TransactionTypeCode: transactionTypeCode,
            FinancialTransactionType: this.finDocType,
            TotalPrice: this.form.controls.value?.value ?? totalPrice,
            ReasonCode: this.form.controls.reason?.value ?? fixedReason,
            ReasonText: this.reasonOptions.find( r => r.value === this.form.controls.reason?.value)?.label ?? fixedReason,
            CurrencyISOCode: currencyCode ?? this.loginService.user.CurrencyCode,
            TenderType: tenderType,
            TenderSubType: tenderSubType,
            Note: this.form.controls.note?.value ?? null,
            Payment: {
                Amount: this.form.controls.value?.value ?? totalPrice,
                CurrencyISOCode: currencyCode ?? this.loginService.user.CurrencyCode,
                TenderType: tenderType
            },
            Attributes: attributes,
            DenominationItems: denominationItems,
            AdditionalJsonData: additionalJsonData,
        };
        
            const finTransaction = await this.posDataService.createFinancialTransaction(request);

            if (!finTransaction) {
                 this.spinner.hide();
                this.popUpDialogInformalService.confirm({
                    message: "",
                    title: this.translate.instant("webPos.Errors.Error"),
                    type: PopUpInformalType.Success,
                });
                return;
            }

            this.spinner.hide();
            
            const finResult = await this.popUpDialogInformalService.confirm({
                title: this.translate.instant("webPos.Done"),
                message: "",
                type: PopUpInformalType.Success,
                showPrintReceipt: true,
            });
            

            if (finResult?.printReceipt) {

                const components = finTransaction?.records?.find(r => r.name === 'Header')?.components;

                if (!components || components.length === 0) {
                    return;
                }

                const trxUID = components.find((component): component is { trxUID: string } => {
                    return typeof component === "object" && component !== null && "trxUID" in component;
                })?.trxUID; // [DN.GDA].trans.Headers.HeaderUID
                if (trxUID) {
                    const headerProcessingResult = await this.headerProcessingService.monitorHeader(trxUID);
                    const headerIdForPrint = this.headerProcessingService.getHeaderIdFromResult(headerProcessingResult);

                    if (headerIdForPrint) {
                        await this.reportExportService.downloadPdf(
                            undefined,
                            [{ key: 'HeaderID', value: headerIdForPrint.toString() }],
                            true
                        );
                    }
                }                
            }
            this.close();
          
           
    }  

    close() {
        this.onClose?.emit();
    }    
}


